/* eslint-disable complexity */

import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Typography from 'carbon-react/lib/components/typography';
import Password from 'carbon-react/lib/components/password';
import Button from 'carbon-react/lib/components/button';

import { FormattedMessage, IntlShape, useIntl } from '@futrli/localisation';
import { createPasswordValidator, isPending } from '@futrli/shared-services';
import { UILoaderSpinner } from '@futrli/ui-components';

import { changeUserPassword } from '../../auth/change-password/redux-utils/actions';
import { AuthError, StoreState } from '../../core/types';
import { getErrorSelector } from '../../core/redux-utils/selectors';
import { CHANGE_PASSWORD } from '../../auth/change-password/contants';
import * as styles from '../my-profile.scss';
import { resetError } from '../../core/redux-utils/actions';

type TouchedValues = Record<string, boolean>;

const getOldPasswordError = (
  submitError: AuthError,
  isLimitExceededException: boolean,
  intl: IntlShape,
  touchedOldPassword: boolean,
  oldPasswordValid: boolean
) => {
  return submitError
    ? isLimitExceededException
      ? intl.formatMessage({
          id: 'change_password.attempt_limit_exceeded',
          defaultMessage: 'Attempt limit exceeded, please try after some time.'
        })
      : intl.formatMessage({ id: 'change_password.incorrect_password', defaultMessage: 'Incorrect password' })
    : touchedOldPassword &&
        !oldPasswordValid &&
        intl.formatMessage({
          id: 'validation.invalid_password_description',
          defaultMessage:
            'Sorry, your password must be at least 9 or more characters and contain a capital letter, number and a symbol.'
        });
};

export const ChangePassword = () => {
  const isChangePasswordLoading = useSelector((state: StoreState) => isPending(state.loadingRequests.CHANGE_PASSWORD));

  const [formValues, setFormValues] = useState({
    oldPassword: '',
    newPassword: '',
    newPasswordConfirm: ''
  });

  const [touchedValues, setTouchedValues] = useState<TouchedValues>({
    oldPassword: false,
    newPassword: false,
    newPasswordConfirm: false
  });

  const intl = useIntl();

  const dispatch = useDispatch();

  const submitError = useSelector((state: StoreState) => getErrorSelector(state.authReducers, CHANGE_PASSWORD.ACTION));

  const passwordValidator = createPasswordValidator();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'oldPassword' && submitError) {
      dispatch(resetError(CHANGE_PASSWORD));
    }
    setFormValues({
      ...formValues,
      [name]: value
    });
    if (!touchedValues[name]) {
      setTouchedValues({
        ...touchedValues,
        [name]: true
      });
    }
  };

  const setInitialValues = () => {
    setFormValues({
      oldPassword: '',
      newPassword: '',
      newPasswordConfirm: ''
    });
    setTouchedValues({ oldPassword: false, newPassword: false, newPasswordConfirm: false });
  };

  const onSubmit = async () => {
    await changeUserPassword(formValues.oldPassword, formValues.newPassword)(dispatch);
    setInitialValues();
  };

  const oldPasswordValid = passwordValidator(formValues.oldPassword).isValid;
  const newPasswordValid = passwordValidator(formValues.newPassword).isValid;
  const confirmPasswordValid = formValues.newPassword === formValues.newPasswordConfirm;

  const isLimitExceededException = submitError?.code === 'LimitExceededException';

  return (
    <div className={styles.container}>
      {isChangePasswordLoading ? (
        <UILoaderSpinner />
      ) : (
        <>
          <Typography mb="15px" fontWeight="700" fontSize="18px">
            <FormattedMessage id="change_password.change_password" defaultMessage="Change password" />
          </Typography>

          <Password
            label={intl.formatMessage({
              id: 'change_password.old_password',
              defaultMessage: 'Old password'
            })}
            placeholder={intl.formatMessage({
              id: 'change_password.enter_old_password',
              defaultMessage: 'Enter old password'
            })}
            value={formValues.oldPassword}
            onChange={handleInputChange}
            name="oldPassword"
            mb="16px"
            error={
              (!oldPasswordValid && touchedValues.oldPassword) || !!submitError
                ? getOldPasswordError(
                    submitError,
                    isLimitExceededException,
                    intl,
                    touchedValues.oldPassword,
                    oldPasswordValid
                  )
                : ''
            }
            size="large"
          />
          <Password
            label={intl.formatMessage({
              id: 'change_password.new_password',
              defaultMessage: 'New password'
            })}
            placeholder={intl.formatMessage({
              id: 'change_password.enter_new_password',
              defaultMessage: 'Enter new password'
            })}
            value={formValues.newPassword}
            onChange={handleInputChange}
            name="newPassword"
            mb="16px"
            error={
              !newPasswordValid && touchedValues.newPassword
                ? intl.formatMessage({
                    id: 'validation.invalid_password_description',
                    defaultMessage:
                      'Sorry, your password must be at least 9 or more characters and contain a capital letter, number and a symbol.'
                  })
                : ''
            }
            size="large"
          />
          <Password
            label={intl.formatMessage({
              id: 'change_password.confirm_new_password',
              defaultMessage: 'Confirm new password'
            })}
            placeholder={intl.formatMessage({
              id: 'change_password.enter_new_password_again',
              defaultMessage: 'Enter new password again'
            })}
            value={formValues.newPasswordConfirm}
            onChange={handleInputChange}
            name="newPasswordConfirm"
            error={
              !confirmPasswordValid && touchedValues.newPasswordConfirm
                ? intl.formatMessage({
                    id: 'change_password.passwords_do_not_match',
                    defaultMessage: "Passwords don't match"
                  })
                : ''
            }
            size="large"
          />
          <div style={{ display: 'flex', marginTop: 30 }}>
            <Button
              disabled={!oldPasswordValid || !newPasswordValid || !confirmPasswordValid || !!submitError}
              onClick={onSubmit}
              ml="auto"
              buttonType="primary"
              size="medium"
            >
              <FormattedMessage id="change_password.change_password" defaultMessage="Change password" />
            </Button>
          </div>
        </>
      )}
    </div>
  );
};
